// src/pages/TrackOrder.js
import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import './TrackOrder.css';

export default function TrackOrder() {
  const { orderNumber } = useParams();
  const [searchParams] = useSearchParams();
  const [orderStatus, setOrderStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Order status states
  const STATUS_STAGES = [
    { key: 'confirmed', label: 'Order Confirmed', icon: '✓', description: 'Your order has been received' },
    { key: 'preparing', label: 'Preparing', icon: '🍳', description: 'Chef is preparing your meal' },
    { key: 'ready', label: 'Ready for Pickup', icon: '📦', description: 'Meal is ready' },
    { key: 'out_for_delivery', label: 'Out for Delivery', icon: '🚗', description: 'On the way to you' },
    { key: 'delivered', label: 'Delivered', icon: '✅', description: 'Enjoy your meal!' }
  ];

  useEffect(() => {
    loadOrderStatus();
    // Poll for updates every 30 seconds
    const interval = setInterval(loadOrderStatus, 30000);
    return () => clearInterval(interval);
  }, [orderNumber]);

  const loadOrderStatus = async () => {
    try {
      setLoading(true);
      const orderNum = orderNumber || searchParams.get('order');
      
      // Try to load from localStorage first
      const savedOrder = localStorage.getItem(`order_${orderNum}`);
      if (savedOrder) {
        const orderData = JSON.parse(savedOrder);
        console.log('📦 Order loaded from localStorage:', orderData);
        
        // Convert to tracking format
        setOrderStatus({
          orderNumber: orderData.orderNumber,
          status: 'confirmed', // Default status for new orders
          customer: orderData.customer,
          items: orderData.items,
          total: orderData.total,
          deliveryAddress: orderData.deliveryAddress,
          deliveryDate: orderData.deliveryDate,
          deliveryTime: orderData.deliveryTime,
          statusHistory: [
            { status: 'confirmed', timestamp: orderData.placedAt }
          ]
        });
        setError(null);
        setLoading(false);
        return;
      }
      
      // Try API if not in localStorage
      const response = await fetch(`/api/orders/status?orderNumber=${orderNum}`);
      
      if (!response.ok) {
        throw new Error('Order not found');
      }

      const data = await response.json();
      console.log('📦 Order status loaded from API:', data);
      setOrderStatus(data.order);
      setError(null);
    } catch (err) {
      console.error('Error loading order:', err);
      setError(err.message);
      // Demo data for testing
      setOrderStatus({
        orderNumber: orderNumber || searchParams.get('order') || 'ORD-DEMO',
        status: 'out_for_delivery',
        customer: 'Demo Customer',
        items: [
          { name: 'Lobster Roll', quantity: 2 },
          { name: 'Boston Clam Chowder', quantity: 1 }
        ],
        total: 6600,
        deliveryAddress: '123 Main St, Boston, MA 02101',
        deliveryDate: new Date().toISOString().split('T')[0],
        deliveryTime: '17:00-19:00',
        chef: {
          name: 'Chef Stu Rader',
          phone: '(555) 123-4567',
          photo: 'https://imagedelivery.net/YQhisQdXT8651Bk9Z7c9KQ/5ca06738-6487-4c23-1c2c-aecd8b313600/public'
        },
        driver: {
          name: 'Driver Mike',
          phone: '(555) 987-6543'
        },
        statusHistory: [
          { status: 'confirmed', timestamp: new Date(Date.now() - 3600000).toISOString() },
          { status: 'preparing', timestamp: new Date(Date.now() - 2400000).toISOString() },
          { status: 'ready', timestamp: new Date(Date.now() - 1200000).toISOString() },
          { status: 'out_for_delivery', timestamp: new Date(Date.now() - 600000).toISOString() }
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  const getCurrentStageIndex = () => {
    if (!orderStatus) return 0;
    return STATUS_STAGES.findIndex(stage => stage.key === orderStatus.status);
  };

  const formatPrice = (cents) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  if (loading && !orderStatus) {
    return (
      <div className="track-order-page">
        <div className="track-container">
          <div className="loading">Loading order status...</div>
        </div>
      </div>
    );
  }

  if (!orderStatus) {
    return (
      <div className="track-order-page">
        <div className="track-container">
          <div className="error">
            <h2>Order Not Found</h2>
            <p>We couldn't find order: {orderNumber || searchParams.get('order')}</p>
            <p>Please check your order number and try again.</p>
          </div>
        </div>
      </div>
    );
  }

  const currentStage = getCurrentStageIndex();

  return (
    <div className="track-order-page">
      <div className="track-container">
        <h1>Track Your Order</h1>
        <div className="order-number">Order #{orderStatus.orderNumber}</div>

        {error && (
          <div className="demo-banner">
            <p>🎭 <strong>Demo Mode:</strong> This is sample tracking data. Real order tracking will connect to your database.</p>
          </div>
        )}

        {/* Status Timeline */}
        <div className="status-timeline">
          {STATUS_STAGES.map((stage, index) => {
            const isCompleted = index <= currentStage;
            const isCurrent = index === currentStage;
            const statusRecord = orderStatus.statusHistory?.find(h => h.status === stage.key);
            
            return (
              <div key={stage.key} className="timeline-item-wrapper">
                <div className={`timeline-item ${isCompleted ? 'completed' : ''} ${isCurrent ? 'current' : ''}`}>
                  <div className="timeline-icon">{stage.icon}</div>
                  <div className="timeline-content">
                    <h3>{stage.label}</h3>
                    <p>{stage.description}</p>
                    {statusRecord && (
                      <span className="timeline-time">{formatTime(statusRecord.timestamp)}</span>
                    )}
                  </div>
                </div>
                {index < STATUS_STAGES.length - 1 && (
                  <div className={`timeline-connector ${isCompleted ? 'completed' : ''}`}></div>
                )}
              </div>
            );
          })}
        </div>

        {/* Delivery Details */}
        <div className="delivery-details">
          <div className="detail-card">
            <h3>Delivery Information</h3>
            <p><strong>Address:</strong> {orderStatus.deliveryAddress}</p>
            <p><strong>Date:</strong> {new Date(orderStatus.deliveryDate).toLocaleDateString()}</p>
            <p><strong>Time:</strong> {orderStatus.deliveryTime}</p>
          </div>

          {orderStatus.chef && (
            <div className="detail-card">
              <h3>Your Chef</h3>
              <div className="chef-info">
                {orderStatus.chef.photo && (
                  <img src={orderStatus.chef.photo} alt={orderStatus.chef.name} className="chef-avatar" />
                )}
                <div>
                  <p><strong>{orderStatus.chef.name}</strong></p>
                  <p>{orderStatus.chef.phone}</p>
                </div>
              </div>
            </div>
          )}

          {orderStatus.driver && orderStatus.status === 'out_for_delivery' && (
            <div className="detail-card driver-card">
              <h3>Your Driver</h3>
              <div className="driver-info">
                <div className="driver-icon">🚗</div>
                <div>
                  <p><strong>{orderStatus.driver.name}</strong></p>
                  <p>{orderStatus.driver.phone}</p>
                  <p className="eta-text">🕐 Estimated arrival: 15-20 minutes</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Order Items */}
        <div className="order-items-summary">
          <h3>Your Order</h3>
          {orderStatus.items?.map((item, index) => (
            <div key={index} className="order-item-row">
              <span>{item.quantity}x {item.name}</span>
            </div>
          ))}
          <div className="order-total">
            <strong>Total:</strong> {formatPrice(orderStatus.total)}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="track-actions">
          {orderStatus.status !== 'delivered' && (
            <button className="btn-refresh" onClick={loadOrderStatus}>
              🔄 Refresh Status
            </button>
          )}
          <a href="/order-now" className="btn-order-again">Order Again</a>
        </div>
      </div>
    </div>
  );
}

